/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import TradeList from 'src/components/Trade/TradeList';
import TradeListTablePlaceholder from 'src/components/Trade/TradeList/TradeListTable/Placeholder';

import useRouteQueryRetry from './helpers/useRouteQueryRetry';

export const tradeListRouteQuery = graphql`
  query TradeListRoute_Query(
    $uuid: ID!
  ) {
      ...TradeList
        @arguments(
          uuid: $uuid
        )
  }
`;

const tradeListRoutePropsShape = PropTypes.shape({
  match: PropTypes.shape({
    location: PropTypes.shape({
      query: PropTypes.shape({
        date: PropTypes.string,
      }),
    }),
    params: PropTypes.shape({
      uuid: PropTypes.string,
    }),
  }),
});

/**
 * Renders `TradeList` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Found-relay route props (match/router/location).
 * @param {string} [props.queryDate] - ISO date string for the trade list date (from the URL query).
 *   Absent when the URL contains no date query parameter.
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} trade list route content.
 */
function TradeListPreloadedQueryRenderer({
  routeProps,
  queryRef,
  queryDate = null,
}) {
  const queryData = usePreloadedQuery(tradeListRouteQuery, queryRef);

  return (
    <TradeList
      {...routeProps}
      {...queryData}
      queryDate={queryDate}
    />
  );
}

TradeListPreloadedQueryRenderer.propTypes = {
  routeProps: tradeListRoutePropsShape.isRequired,
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
  queryDate: PropTypes.string,
};

/**
 * Hook-safe renderer for the `TradeList` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @returns {React.ReactNode} trade list route content.
 */
function TradeListRouteRenderer({
  routeProps,
}) {
  const { match } = routeProps || {};
  const { location } = match || {};
  const { query } = location || {};
  const { date: queryDate } = query || {};
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    tradeListRouteQuery,
    routeProps,
    { logLabel: 'TradeListRoute' },
  );

  return (
    <PlaceholderErrorBoundary
      placeholderComponent={TradeListTablePlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {queryRef ? (
          <TradeListPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
            queryDate={queryDate}
          />
        ) : (
          <TradeList
            {...routeProps}
            queryDate={queryDate}
          />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

TradeListRouteRenderer.propTypes = {
  routeProps: tradeListRoutePropsShape.isRequired,
};

export default TradeListRouteRenderer;
